import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { FlexColumn, Gap } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text/index';

import { messages } from './messages';
import { getSanitizedName } from '../../../components/data-driven-logo/name-utils';
import HelpArticleLink from '../../../components/help-article-link';
import { SimpleModal } from '../../../components/simple-modal';
import { linkIDs } from '../../../services/link-service/links';
import Global from '../../../store/global-selectors';

/**
 * @typedef {import('ui-components/prop-types/modal-framework-methods').ModalFrameworkMethodsPropType} ModalFrameworkMethodsPropType
 */
/**
 * RateShopperPickupPrompt component - prompts user to schedule a pickup
 * @param {object} props
 * @param {string} props.sellerLabelProviderId - ID of the seller label provider
 * @param {() => void} props.createLabelAction - Action creator function that returns the action to create the shipping label
 * @param {boolean} [props.andPrint] - Whether to print labels after creation
 * @param {ModalFrameworkMethodsPropType} props.modalFrameworkMethods
 * @returns {React.ReactElement} The RateShopperPickupPrompt component
 */
export const RateShopperPickupPrompt = ({ sellerLabelProviderId, createLabelAction, modalFrameworkMethods, andPrint = false }) => {
  const dispatch = useDispatch();
  const { closeModal } = modalFrameworkMethods;
  const sellerLabelProviders = useSelector(Global.selectSellerLabelProviders);
  const labelProviders = useSelector(Global.selectReferenceDatagetProviders);

  const sellerLabelProvider = sellerLabelProviders.find((provider) => provider.sellerLabelProviderId === sellerLabelProviderId) || {};
  const labelProvider = labelProviders.find((provider) => provider.labelProviderId === sellerLabelProvider.labelProviderId) || {};
  const labelProviderName = getSanitizedName(labelProvider.name || '');

  const closeWithEffects = () => {
    closeModal();
  };

  const handleCreateLabel = () => {
    closeWithEffects();
    dispatch(createLabelAction());
  };

  return (
    <>
      <SimpleModal
        actionMessage={<FormattedMessage {...messages.createFirstLabel} values={{ andPrint }} />}
        cancelMessage={messages.selectDifferentCarrier}
        headerMessage={labelProviderName}
        headerTextType={TextType.H3}
        onAction={handleCreateLabel}
        onCancel={closeWithEffects}
        onClose={closeWithEffects}
        size='small'
      >
        <FlexColumn gap={Gap.Space16}>
          <Text textType={TextType.H3} value={<FormattedMessage {...messages.needToSchedulePickupTitle} />} />
          <Text
            textType={TextType.Paragraph}
            value={<FormattedMessage {...messages.requiresScheduledPickupMessage} values={{ labelProviderName }} />}
          />
          <Text textType={TextType.Paragraph} value={<FormattedMessage {...messages.bulkcreateAmazonLabelWithPickup} />} />
          <Text
            textType={TextType.Paragraph}
            value={
              <HelpArticleLink articleId={linkIDs.AmazonShippingPickup}>
                <FormattedMessage {...messages.learnMoreMessage} values={{ labelProviderName }} />
              </HelpArticleLink>
            }
          />
        </FlexColumn>
      </SimpleModal>
    </>
  );
};
